import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { myOrdersQuery } from "@/lib/queries";
import { formatMoney } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/app/orders")({
  head: () =>
    privatePageMetadata(
      "/app/orders",
      "Orders | BeHot.Now",
      "Your BeHot.Now orders.",
    ),
  component: OrdersPage,
});

function OrdersPage() {
  const { t } = useT();
  const { user } = useAuth();
  const { data: orders = [], isLoading } = useQuery(myOrdersQuery(user?.id));

  return (
    <div className="space-y-3">
      {isLoading && (
        <div className="card-surface p-6 text-sm text-muted-foreground">{t("dashboard.orders.loading")}</div>
      )}
      {!isLoading && orders.length === 0 && (
        <div className="card-surface p-6 text-sm text-muted-foreground">
          {t("dashboard.orders.empty")}
        </div>
      )}
      {orders.map((o) => (
        <div key={o.id} className="card-surface flex flex-wrap items-center gap-3 p-4">
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold">
              {o.listing?.name ?? t("dashboard.orders.boost")}
              {o.category ? ` · ${o.category.name}` : ""}
            </p>
            <p className="text-xs text-muted-foreground">
              {new Date(o.created_at).toLocaleString()}
              {o.invoice_number ? ` · ${o.invoice_number}` : ""}
            </p>
          </div>
          <Badge variant={o.status === "paid" ? "secondary" : "outline"}>{o.status}</Badge>
          <span className="font-display font-bold">
            {formatMoney(o.amount_paid_cents || o.amount_due_cents, o.currency)}
          </span>
          {o.status === "paid" && (
            <Button asChild variant="outline" size="sm">
              <Link to="/app/invoices/$id" params={{ id: o.id }}>
                {t("dashboard.orders.invoice")}
              </Link>
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
